import React from "react";
import Tooltip from "@edaz/edazkit/Tooltip";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay/index";
import { DataTable } from "@frui.ts/dataviews/dist";
import type { ColumnDefinition } from "@frui.ts/dataviews/dist/dataTypes";
import { registerView, View } from "@frui.ts/views";
import type { ViewComponent } from "@frui.ts/views";
import { observer, Observer } from "mobx-react-lite";
import { formatDateTime, formatLicensePlate, translateGeneral } from "utils/localizations";
import Paginator from "views/common/paginator";
import EmailMessageViewModel from "viewModels/callCenter/search/emailMessage/emailMessageViewModel";
import type SentEmailMessage from "entities/sentEmailMessageResponse";
import { emptyValue } from "utils/emptyValue";

const EmailMessageView: ViewComponent<EmailMessageViewModel> = observer(({ vm }) => {
  const headerClassName = "py-4 text-wrap align-middle";
  const wrapClassName = "text-wrap";
  const noWrapClassName = "text-nowrap";
  const columns: ColumnDefinition<SentEmailMessage>[] = [
    {
      title: vm.translateAttribute("_vehicle"),
      headerClassName: headerClassName,
      cellClassName: "font-weight-bold",
      cellProps: _ => ({ style: { minWidth: 100 } }),
      valueFormatter: props => {
        if (props.item.charge?.vehicle) {
          const newLicensePlate = props.item.charge.vehicle.licensePlate;
          const originalLicensePlate = vm.filter.licensePlate;

          const licensePlateChangedInformation = originalLicensePlate && newLicensePlate !== originalLicensePlate;
          if (licensePlateChangedInformation && newLicensePlate && props.item.charge.vehicle) {
            const licensePlateChangeTooltip = vm
              .translate("licensePlateChangedMessages")
              .replace("%originalLicensePlate%", originalLicensePlate)
              .replace("%newLicensePlate%", newLicensePlate);

            return (
              <Tooltip id="license-plate-tooltip" className="license-plate-tooltip" tooltip={licensePlateChangeTooltip ?? ""}>
                <span className="font-weight-bold text-nowrap text-secondary link-underline">
                  <span>{formatLicensePlate(props.item.charge.vehicle)}</span>
                </span>
              </Tooltip>
            );
          }
          return formatLicensePlate(props.item.charge?.vehicle);
        } else {
          return vm.translate("vehicles", props.item.chargesCount).replace("%count%", props.item.chargesCount.toString());
        }
      },
    },
    {
      title: vm.translateAttribute("sentAt"),
      property: "sentAt",
      cellClassName: wrapClassName,
      headerClassName: headerClassName,
      valueFormatter: props => (props.item.sentAt ? formatDateTime(props.item.sentAt) : emptyValue),
    },
    {
      title: vm.translateAttribute("subject"),
      property: "subject",
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
      valueFormatter: props => props.item.subject ?? emptyValue,
    },
    {
      title: vm.translateAttribute("recipient"),
      property: "recipient",
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
      valueFormatter: props => props.item.recipient ?? emptyValue,
    },
    {
      title: vm.translateAttribute("languageCode"),
      property: "languageCode",
      headerClassName: headerClassName,
      valueFormatter: props => props.item.languageCode ?? emptyValue,
    },
    {
      title: vm.translateAttribute("_chargeType"),
      headerClassName: headerClassName,
      property: "charge",
      cellClassName: wrapClassName,
      valueFormatter: props => (props.item.charge?.priceListItemId ? vm.translateChargeType(props.item.charge) : emptyValue),
    },
    {
      title: vm.translateAttribute("_fuelType"),
      headerClassName: headerClassName,
      property: "chargesCount",
      cellClassName: wrapClassName,
      valueFormatter: props => (props.item.charge ? vm.translateFuelType(props.item.charge) : emptyValue),
    },
    {
      title: vm.translateAttribute("_validSince"),
      cellProps: _ => ({ style: { minWidth: 100 } }),
      valueFormatter: props => (props.item.charge?.validSince ? formatDateTime(props.item.charge?.validSince) : emptyValue),
      headerClassName: headerClassName,
    },
    {
      title: vm.translateAttribute("_validUntil"),
      cellProps: _ => ({ style: { minWidth: 100 } }),
      valueFormatter: props => (props.item.charge?.validUntil ? formatDateTime(props.item.charge?.validUntil) : emptyValue),
      headerClassName: headerClassName,
    },
    {
      title: vm.translateAttribute("orderIdent"),
      property: "orderIdent",
      headerClassName: headerClassName,
      valueFormatter: props => props.item.orderIdent ?? emptyValue,
    },
  ];

  return (
    <>
      <LoaderOverlay loading={vm.loading}>
        <DataTable
          headerRowClassName="bg-white"
          columns={columns}
          items={vm.items}
          itemKey={vm.idProperty}
          className="mt-n4 table table-striped"
        />
        {vm.loading && vm.items.length === 0 && (
          <div className="bg-light mt-n4 mb-4 px-4 py-3 border-bottom text-muted">{translateGeneral("loading")}</div>
        )}
        {!vm.loading && vm.items.length === 0 && (
          <div className="bg-light mt-n4 mb-4 px-4 py-3 border-bottom text-muted">{translateGeneral("empty")}</div>
        )}
        <Paginator
          className="justify-content-center mt-4 mb-2"
          pagingInfo={vm.currentPaging}
          filter={vm.pagingFilter}
          onPageChanged={() => vm.loadData()}
        />
      </LoaderOverlay>
      <Observer>{() => <View vm={vm.activeChild} />}</Observer>
    </>
  );
});

registerView(EmailMessageView, EmailMessageViewModel);
